import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { safeInvoke } from '../lib/safeInvoke';
import { ZODIAC_SIGNS } from '../lib/astrology';
import { TextSkeleton } from '../components/shared/TextSkeleton';
import { ReadingActions } from '../components/shared/ReadingActions';
import { ReadingFeedback } from '../components/shared/ReadingFeedback';

interface SynastryResult {
  score: number;
  sections: {
    energiaGeral?: string;
    tensaoHarmonia?: string;
    comoSeAfetam?: string;
    casalUnidade?: string;
    missaoKarmica?: string;
    energiasAgora?: string;
  };
  readingId: string | null;
  text: string;
}

const TABS = [
  { key: 'energiaGeral',   label: '🌟 Energia Geral' },
  { key: 'tensaoHarmonia', label: '⚡ Tensão e Harmonia' },
  { key: 'comoSeAfetam',  label: '🏠 Como se Afetam' },
  { key: 'casalUnidade',  label: '✨ O Casal como Unidade' },
  { key: 'missaoKarmica', label: '🌍 Missão Kármica' },
  { key: 'energiasAgora', label: '🔭 Energias de Agora' },
];

function ScoreBar({ score }: { score: number }) {
  const label = score >= 80 ? 'Alma gêmea astrológica ✦' : score >= 65 ? 'Grande sintonia entre vocês' : score >= 50 ? 'Conexão com potencial' : score >= 35 ? 'Energia que transforma' : 'Aprendizado mútuo intenso';
  const color = score >= 65 ? '#4ade80' : score >= 45 ? '#F5C842' : '#fb923c';
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <span style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>Compatibilidade</span>
        <span style={{ fontSize: '1.25rem', fontWeight: 700, color, fontFamily: 'var(--font-display)' }}>{score}<span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>/100</span></span>
      </div>
      <div style={{ height: '8px', backgroundColor: 'var(--bg-base)', borderRadius: 'var(--radius-full)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${score}%`, backgroundColor: color, borderRadius: 'var(--radius-full)', transition: 'width 1s ease' }} />
      </div>
      <p style={{ fontSize: 'var(--text-caption)', color, marginTop: '0.4rem', fontWeight: 600 }}>{label}</p>
    </div>
  );
}

type Phase = 'validating' | 'invalid' | 'form' | 'loading' | 'result' | 'done';

export function InvitePage() {
  const { token }   = useParams<{ token: string }>();
  const navigate    = useNavigate();
  const { profile } = useAuth();

  const [phase,         setPhase]         = useState<Phase>('validating');
  const [inviterName,   setInviterName]   = useState('');
  const [myName,        setMyName]        = useState('');
  const [mySign,        setMySign]        = useState('');
  const [myBirthDate,   setMyBirthDate]   = useState('');
  const [result,        setResult]        = useState<SynastryResult | null>(null);
  const [activeTab,     setActiveTab]     = useState('energiaGeral');
  const [error,         setError]         = useState<string | null>(null);

  useEffect(() => {
    if (!token) { setPhase('invalid'); return; }
    validateToken();
  }, [token]);

  async function validateToken() {
    const { data, error } = await supabase
      .from('synastry_invite_tokens')
      .select('id, user_id, used_at, profiles:user_id(full_name)')
      .eq('token', token)
      .maybeSingle();

    if (error || !data) { setPhase('invalid'); return; }
    if (data.used_at) { setPhase('done'); return; }

    const name = (data.profiles as unknown as { full_name: string } | null)?.full_name?.split(' ')[0] ?? 'alguém especial';
    setInviterName(name);

    // If already logged in and have birth data, pre-fill
    if (profile?.full_name) setMyName(profile.full_name.split(' ')[0]);
    if (profile?.sun_sign) setMySign(profile.sun_sign);
    if (profile?.birth_date) setMyBirthDate(profile.birth_date);

    setPhase('form');
  }

  async function submit() {
    if (!myName || !mySign || !myBirthDate) return;
    setPhase('loading');
    setError(null);

    // Mark token as used
    await supabase
      .from('synastry_invite_tokens')
      .update({ used_at: new Date().toISOString() })
      .eq('token', token);

    const { data, fromFallback } = await safeInvoke<SynastryResult>('synastry', {
      body:     { partnerName: myName, partnerBirthDate: myBirthDate, partnerSunSign: mySign, inviteToken: token },
      fallback: { score: 0, sections: {}, readingId: null, text: '' },
      timeout:  30_000,
    });

    if (fromFallback || !data.text) {
      setError('Análise temporariamente indisponível. Tente novamente ✦');
      setPhase('form');
      return;
    }
    setResult(data);
    setPhase('result');
  }

  if (phase === 'validating') {
    return (
      <div className="page-container" style={{ textAlign: 'center', paddingTop: '4rem' }}>
        <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>Verificando convite...</p>
      </div>
    );
  }

  if (phase === 'invalid') {
    return (
      <div className="page-container" style={{ textAlign: 'center', paddingTop: '4rem' }}>
        <span style={{ fontSize: '3rem', display: 'block', marginBottom: '1rem' }}>💫</span>
        <h1 className="font-display" style={{ color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Convite inválido</h1>
        <p style={{ color: 'var(--text-tertiary)', marginBottom: '1.5rem' }}>Este link de convite não existe ou expirou.</p>
        <button className="btn btn-primary" onClick={() => navigate('/')}>Ir para o Professya</button>
      </div>
    );
  }

  if (phase === 'done') {
    return (
      <div className="page-container" style={{ textAlign: 'center', paddingTop: '4rem' }}>
        <span style={{ fontSize: '3rem', display: 'block', marginBottom: '1rem' }}>✅</span>
        <h1 className="font-display" style={{ color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Convite já utilizado</h1>
        <p style={{ color: 'var(--text-tertiary)', marginBottom: '1.5rem' }}>Este link já foi usado para fazer a sinastria.</p>
        <button className="btn btn-primary" onClick={() => navigate('/')}>Conhecer o Professya</button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-base)', padding: '1.5rem 1rem', maxWidth: '480px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <span style={{ fontSize: '2.5rem', display: 'block', marginBottom: '0.75rem' }}>💫</span>
        <h1 className="font-display" style={{ fontSize: 'clamp(1.25rem,4vw,1.75rem)', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
          {inviterName} quer fazer a sinastria com você!
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          Compatibilidade Astrológica · Professya
        </p>
      </div>

      {phase === 'form' && (
        <div className="card">
          <h2 className="font-display" style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '1.25rem' }}>
            Seus dados
          </h2>

          <div className="form-group">
            <label className="form-label" htmlFor="inv-name">Seu nome</label>
            <input id="inv-name" type="text" className="form-input" value={myName} onChange={e => setMyName(e.target.value)} placeholder="Como você se chama?" />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="inv-sign">Seu signo solar</label>
            <select id="inv-sign" className="form-select" value={mySign} onChange={e => setMySign(e.target.value)}>
              <option value="">Selecione o signo</option>
              {ZODIAC_SIGNS.map(s => (
                <option key={s.key} value={s.key}>{s.emoji} {s.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="inv-date">Sua data de nascimento</label>
            <input id="inv-date" type="date" className="form-input" value={myBirthDate}
              onChange={e => setMyBirthDate(e.target.value)}
              max={new Date().toISOString().slice(0, 10)} />
          </div>

          {error && (
            <p style={{ color: '#fca5a5', fontSize: 'var(--text-caption)', marginBottom: '1rem' }}>{error}</p>
          )}

          <button className="btn btn-primary btn-block" onClick={submit}
            disabled={!myName || !mySign || !myBirthDate}>
            Ver nossa compatibilidade ✦
          </button>

          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '1rem', textAlign: 'center', lineHeight: 1.5 }}>
            Seus dados são usados apenas para calcular a sinastria e não são armazenados.
          </p>
        </div>
      )}

      {phase === 'loading' && (
        <div className="card">
          <TextSkeleton lines={5} message={`Calculando a sinastria entre você e ${inviterName}...`} />
        </div>
      )}

      {phase === 'result' && result && (
        <div className="animate-fade-in">
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem', fontSize: 'var(--text-label)' }}>
              💫 {myName} & {inviterName}
            </p>
            <ScoreBar score={result.score} />
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '0.35rem', overflowX: 'auto', marginBottom: '1rem', paddingBottom: '0.25rem' }}>
            {TABS.map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                className={activeTab === t.key ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
                style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                {t.label}
              </button>
            ))}
          </div>

          <div className="card animate-fade-in" key={activeTab} style={{ marginBottom: '1.5rem' }}>
            <div className="reading-text">
              {((result.sections as Record<string, string>)[activeTab] || result.text)
                .split('\n').filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>

          <ReadingActions pageName="Sinastria" reading={result.text} signEmoji="💫" />
          <ReadingFeedback feature="synastry" readingId={result.readingId ?? undefined} />

          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '1rem' }}>
              Gostou? Faça o seu próprio mapa astrológico no Professya!
            </p>
            <button className="btn btn-primary" onClick={() => navigate('/register')}>
              Criar minha conta grátis ✦
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
